import { inject, Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { signal } from '@angular/core';

/**
 * Servicio para controlar la visibilidad del splash screen.
 * Se muestra automáticamente cuando comienza una navegación y se oculta cuando:
 * 1. Termina la navegación de rutas (NavigationEnd)
 * 2. Se llama explícitamente a hideSplash()
 */
@Injectable({
  providedIn: 'root'
})
export class SplashService {
  private router = inject(Router);

  /** Signal que controla la visibilidad del splash (inicia visible) */
  private _isVisible = signal(true);
  isVisible = this._isVisible.asReadonly();

  /** Timeout ID para el fallback de ocultamiento */
  private fallbackTimeoutId: any = null;

  constructor() {
    this.initializeAutoHideAndShow();
  }

  /**
   * Inicializa el mostrado y ocultamiento automático del splash
   * Muestra el splash cuando empieza navegación, lo oculta cuando termina
   */
  private initializeAutoHideAndShow(): void {
    this.router.events.subscribe((event) => {
      // Mostrar splash cuando comienza la navegación
      if (event instanceof NavigationStart) {
        console.log('🚀 Navegación iniciada - mostrando splash');
        this._isVisible.set(true);
        this.clearFallbackTimeout();
        this.initializeFallbackTimeout();
      }
      // Ocultar splash cuando termina la navegación
      if (event instanceof NavigationEnd) {
        console.log('📍 NavigationEnd detectado - ocultando splash en 100ms');
        setTimeout(() => {
          console.log('✅ Ocultando splash');
          this._isVisible.set(false);
          this.clearFallbackTimeout();
        }, 100);
      }
    });
  }

  /**
   * Inicializa timeout de fallback que oculta el splash después de 2 segundos
   * Este es un mecanismo de seguridad en caso de que NavigationEnd no se dispare
   */
  private initializeFallbackTimeout(): void {
    this.fallbackTimeoutId = setTimeout(() => {
      if (this._isVisible()) {
        console.warn(
          '⚠️ Fallback timeout (2s) - forzando ocultamiento del splash'
        );
        this._isVisible.set(false);
      }
    }, 2000); // 2 segundos
  }

  /**
   * Limpia el timeout de fallback
   */
  private clearFallbackTimeout(): void {
    if (this.fallbackTimeoutId) {
      clearTimeout(this.fallbackTimeoutId);
      this.fallbackTimeoutId = null;
    }
  }

  /**
   * Oculta el splash screen manualmente
   */
  hideSplash(): void {
    this._isVisible.set(false);
    this.clearFallbackTimeout();
  }

  /**
   * Muestra el splash screen manualmente
   */
  showSplash(): void {
    this._isVisible.set(true);
    this.clearFallbackTimeout();
    this.initializeFallbackTimeout();
  }

  /**
   * Reinicia el splash (lo muestra de nuevo)
   */
  reset(): void {
    this._isVisible.set(true);
    this.clearFallbackTimeout();
    this.initializeFallbackTimeout();
  }
}
